import { useCallback, useEffect, useRef, useState } from 'react'
import Brainfish from '@brainfish-ai/web-widget'
import { getColorTokenValue } from '@buffer-mono/popcorn'
import { env } from '../../env'
import type { Account } from '../types'
import trackBrainfishOpened from '../../tracking/Brainfish/trackBrainfishOpened'
import trackBrainfishClosed from '../../tracking/Brainfish/trackBrainfishClosed'

interface BrainfishWidgetHookProps {
  user: Account
  onVisibilityChange?: (hidden: boolean) => void
}

/**
 * Converts RGB color object to hex string
 */
function rgbToHex(rgb: { r: number; g: number; b: number }): string {
  const toHex = (n: number) => {
    const hex = Math.round(n).toString(16).padStart(2, '0')
    return hex
  }
  return `#${toHex(rgb.r)}${toHex(rgb.g)}${toHex(rgb.b)}`
}

/**
 * Gets a CSS token value and converts it to hex, or returns undefined if not found
 */
function getTokenHex(tokenName: string): string | undefined {
  const rgb = getColorTokenValue(tokenName)
  return rgb ? rgbToHex(rgb) : undefined
}

// prettier-ignore
/* eslint-disable */
export const useBrainfishWidget = ({
  user,
  onVisibilityChange,
}: BrainfishWidgetHookProps) => {
  const isInitialized = useRef(false)
  const isIdentified = useRef(false)
  const closeVisibilityTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [isReady, setIsReady] = useState(false)
  const [isOpen, setIsOpen] = useState(false)

  useEffect(() => {
    if (isInitialized.current) return

    const initBrainfish = async () => {
      // Let the theme tokens land on the DOM before getTokenHex reads them
      await new Promise((resolve) =>
        requestAnimationFrame(() => requestAnimationFrame(resolve)),
      )

      try {
        await Brainfish.Widgets.init({
          widgetKey: env.BRAINFISH_WIDGET_KEY,
          overrides: {
            theme: {
              inputBorderColor: getTokenHex('color-border-subtle'),
              textBaseColor: getTokenHex('color-text-inverted'),
              inputButtonColor: getTokenHex('color-text-on-light'),
              inputButtonBgColor: getTokenHex('global-color-green-900'),
            },
          },
        })

        if (window.Beacon) {
          window.Beacon('on', 'open', () => Brainfish.Widgets.close())
        }

        setIsReady(true)
      } catch (error) {
        console.error('Failed to initialize Brainfish:', error)
        setIsReady(false)
      }
    }

    initBrainfish()
    isInitialized.current = true
  }, [])

  useEffect(() => {
    if (isIdentified.current || !isReady || !user.id || user.isImpersonation) {
      return
    }

    const identifyBrainfish = async () => {
      try {
        window.BrainfishAnalytics('identify', {
          userId: user.id,
          email: user.email,
          properties: {
            signUpDate: user.createdAt,
            name: user?.name,
            channelCountAcrossAllOrgs: user.channels?.length,
            organizationCount: user.organizations?.length,
            currentOrganizationId: user?.currentOrganization?.id,
            hasVerifiedEmail: user.hasVerifiedEmail,
          },
        })
      } catch (error) {
        console.error('Failed to identify user in Brainfish:', error)
      }
    }

    identifyBrainfish()
    isIdentified.current = true
  }, [user, isReady])

  const openBrainfish = useCallback(() => {
    Brainfish.Widgets.open()
    trackBrainfishOpened({ user })
    setIsOpen(true)
  }, [user])

  const closeBrainfish = () => {
    Brainfish.Widgets.close()
    trackBrainfishClosed({ user })
    setIsOpen(false)
  }

  // Listen for Brainfish widget events and sync visibility
  useEffect(() => {
    const handleBrainfishOpen = (): void => {
      setIsOpen(true)
    }

    const handleBrainfishClose = (): void => {
      // Delay the visibility check so Beacon's open state has settled
      closeVisibilityTimerRef.current = setTimeout(() => {
        // Skip if a Beacon proactive message (e.g. NPS survey) triggered
        // this close — the message-triggered handler owns visibility
        if (window.__beaconMessageActive) return
        const isBeaconOpenNow =
          window?.Beacon && window?.Beacon('info')?.status?.isOpened
        if (!isBeaconOpenNow) {
          onVisibilityChange?.(false)
        }
      }, 0)
      setIsOpen(false)
    }

    window.addEventListener('onBrainfishWidgetOpen', handleBrainfishOpen)
    window.addEventListener('onBrainfishWidgetClose', handleBrainfishClose)

    return () => {
      if (closeVisibilityTimerRef.current) {
        clearTimeout(closeVisibilityTimerRef.current)
      }
      window.removeEventListener('onBrainfishWidgetOpen', handleBrainfishOpen)
      window.removeEventListener('onBrainfishWidgetClose', handleBrainfishClose)
    }
  }, [onVisibilityChange])

  return {
    openBrainfish,
    closeBrainfish,
    isReady,
    isOpen,
  }
}
